export const UPLOAD_MAX_FILE_SIZE_MB = 10;
export const UPLOAD_MAX_FILE_SIZE_BYTES = UPLOAD_MAX_FILE_SIZE_MB * 1024 * 1024;
export const UPLOAD_MAX_MEDIA_FILES = 5;

export const UPLOAD_SIZE_LIMIT_ERROR = 'File exceeds maximum size limit.';

export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';

  if (bytes < 1024) return `${bytes} B`;

  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  const rounded = value >= 10 ? value.toFixed(0) : value.toFixed(1);
  return `${rounded} ${units[unitIndex]}`;
}

export function getUploadSizeLimitMessage(maxMb = UPLOAD_MAX_FILE_SIZE_MB): string {
  return `${UPLOAD_SIZE_LIMIT_ERROR} Maximum size is ${maxMb} MB per file.`;
}

export function getUploadSizeLimitSkipMessage(
  tooLargeCount: number,
  maxMb = UPLOAD_MAX_FILE_SIZE_MB,
): string {
  return `${UPLOAD_SIZE_LIMIT_ERROR} ${tooLargeCount} file${tooLargeCount === 1 ? '' : 's'} skipped. Maximum size is ${maxMb} MB per file.`;
}

// ── Allowed Chat File Types ───────────────────────────────────────────────────

export const IMAGE_MIMES = new Set(['image/jpeg', 'image/png', 'image/gif', 'image/webp']);
export const VIDEO_MIMES = new Set(['video/mp4', 'video/quicktime']);
export const DOCUMENT_MIMES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);

export const ALLOWED_CHAT_MIME_TYPES = new Set([...IMAGE_MIMES, ...VIDEO_MIMES, ...DOCUMENT_MIMES]);

export const CHAT_FILE_ACCEPT_ATTRIBUTE = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
  'video/mp4',
  'video/quicktime',
  '.mp4',
  '.mov',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.pdf',
  '.doc',
  '.docx',
].join(',');

/** Offer supporting docs: PDF, ZIP, DOC(X), PNG, JPG */
export const OFFER_ATTACHMENT_ACCEPT_ATTRIBUTE = [
  'image/jpeg',
  'image/png',
  '.jpg',
  '.jpeg',
  '.png',
  'application/pdf',
  '.pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.doc',
  '.docx',
  'application/zip',
  'application/x-zip-compressed',
  '.zip',
].join(',');

export const ALLOWED_OFFER_ATTACHMENT_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'pdf',
  'doc',
  'docx',
  'zip',
]);

export function validateOfferAttachmentFile(file: File): { valid: boolean; error?: string } {
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  if (!ALLOWED_OFFER_ATTACHMENT_EXTENSIONS.has(ext)) {
    return {
      valid: false,
      error: `Invalid file format (${ext.toUpperCase() || 'unknown'}). Allowed: PDF, ZIP, DOC, DOCX, PNG, JPG.`,
    };
  }
  if (file.size > UPLOAD_MAX_FILE_SIZE_BYTES) {
    return { valid: false, error: getUploadSizeLimitMessage() };
  }
  return { valid: true };
}

export function validateChatFileType(file: File): { valid: boolean; error?: string } {
  const mime = file.type || '';
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  const extToMime: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    mp4: 'video/mp4',
    mov: 'video/quicktime',
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  };

  const resolvedMime = mime || extToMime[ext] || '';

  if (!ALLOWED_CHAT_MIME_TYPES.has(resolvedMime)) {
    return {
      valid: false,
      error: `Invalid file format (${ext.toUpperCase() || 'unknown'}). Allowed formats: JPG, PNG, GIF, WebP, MP4, MOV, PDF, DOC, DOCX.`,
    };
  }

  return { valid: true };
}
